import React, { useEffect, useState } from 'react';
import axios from '../../utils/axiosInstance';
import { toast } from 'react-toastify';
import './UserManagement.css';
import logo from '../../Assets/logo.jpg';

/* ---------- INITIAL STATE ---------- */
const initialFormState = {
  name: '',
  email: '',
  password: '',
  mobile: '',
  gender: '',
  age: '',
  dob: '',
  pincode: '',
  city: '',
  state: '',
  country: '',
  address1: '',
  address2: '',
  wallet: 0,
  panCard: null,
  aadhaarCard: null,
  emergencyContact1: { name: '', phone: '', email: '', relation: '' },
  emergencyContact2: { name: '', phone: '', email: '', relation: '' },
};

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(initialFormState);
  const [editingId, setEditingId] = useState(null);

  /* ---------- FETCH USERS ---------- */
  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get('/users');
      setUsers(res.data.users || []);
    } catch {
      toast.error('Failed to fetch users');
    }
  };

  /* ---------- VERIFY PINCODE ---------- */
  const verifyPincode = async (pincode) => {
    try {
      const res = await axios.post('/users/verify-pincode', { pincode });
      const { city, state, country } = res.data.data;

      setForm((prev) => ({
        ...prev,
        city,
        state,
        country,
      }));

      toast.success('Pincode verified');
    } catch {
      toast.error('Invalid pincode');
      setForm((prev) => ({ ...prev, city: '', state: '', country: '' }));
    }
  };

  /* ---------- HANDLE CHANGE ---------- */
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'pincode') {
      setForm((prev) => ({ ...prev, pincode: value }));
      if (value.length === 6) verifyPincode(value);
      return;
    }

    if (name.startsWith('emergencyContact')) {
      const [contact, field] = name.split('.');
      setForm((prev) => ({
        ...prev,
        [contact]: { ...prev[contact], [field]: value },
      }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  /* ---------- SUBMIT ---------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.city || !form.state) {
      return toast.error('Please verify pincode');
    }

    try {
      const formData = new FormData();

      Object.keys(form).forEach((key) => {
        if (
          key !== 'panCard' &&
          key !== 'aadhaarCard' &&
          typeof form[key] !== 'object'
        ) {
           if (key === 'password' && !form.password) return;

          formData.append(key, form[key]);
        }
      });

      formData.append('emergencyContact1', JSON.stringify(form.emergencyContact1));
      if (form.emergencyContact2?.name) {
        formData.append('emergencyContact2', JSON.stringify(form.emergencyContact2));
      }

      if (form.panCard) formData.append('panCard', form.panCard);
      if (form.aadhaarCard) formData.append('aadhaarCard', form.aadhaarCard);

      if (editingId) {
        await axios.put(`/edit-user/${editingId}`, formData);
        toast.success('User updated');
      } else {
        await axios.post('/create-user', formData);
        toast.success('User created');
      }

      setForm(initialFormState);
      setEditingId(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error saving user');
    }
  };

  /* ---------- EDIT ---------- */
  const handleEdit = (user) => {
    setForm({
      name: user.name || '',
      email: user.email || '',
      password: '',
      mobile: user.mobile || '',
      gender: user.gender || '',
      age: user.age || '',
      dob: user.dob ? user.dob.split('T')[0] : '',
      pincode: user.pincode || '',
      city: user.cityOrPincode || '',
      state: user.state || '',
      country: user.country || '',
      address1: user.address1 || '',
      address2: user.address2 || '',
      wallet: user.wallet || 0,
      panCard: user.panCard || null,
aadhaarCard: user.aadhaarCard || null,
      emergencyContact1: user.emergencyContact1 || initialFormState.emergencyContact1,
      emergencyContact2: user.emergencyContact2 || initialFormState.emergencyContact2,
    });
    setEditingId(user._id);
  };

  /* ---------- DELETE ---------- */
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await axios.delete(`/delete-user/${id}`);
      toast.success('User deleted');
      fetchUsers();
    } catch {
      toast.error('Delete failed');
    }
  };

  /* ---------- APPROVE / REJECT ---------- */
  const approveUser = async (id) => {
    try {
      await axios.post(`/admin/user/approve/${id}`);
      toast.success('User approved');
      fetchUsers();
    } catch {
      toast.error('Approval failed');
    }
  };

  const rejectUser = async (id) => {
    const reason = prompt('Enter rejection reason');
    if (!reason) return;

    try {
      await axios.post(`/admin/user/reject/${id}`, { reason });
      toast.success('User rejected');
      fetchUsers();
    } catch {
      toast.error('Rejection failed');
    }
  };

  /* ---------- RENDER ---------- */
  return (
    <div className="user-management-container">
      <h2 className="title">{editingId ? 'Edit User' : 'Create User'}</h2>

      <form onSubmit={handleSubmit} className="user-form">

        {/* BASIC INFO */}
        {[
          { label: 'Full Name', name: 'name' },
          { label: 'Email', name: 'email', type: 'email' },
          { label: 'Mobile', name: 'mobile' },
          { label: 'Age', name: 'age', type: 'number' },
          { label: 'Address 1', name: 'address1' },
          { label: 'Address 2', name: 'address2' },
        ].map(({ label, name, type = 'text' }) => (
          <div className="form-group" key={name}>
            <label>{label}</label>
            <input name={name} type={type} value={form[name]} onChange={handleChange} required={name !== 'address2'} />
          </div>
        ))}

        {/* DOB */}
        <div className="form-group">
          <label>Date of Birth</label>
          <input type="date" name="dob" value={form.dob} onChange={handleChange} required />
        </div>

        {/* PINCODE */}
        <div className="form-group">
          <label>Pincode</label>
          <input name="pincode" value={form.pincode} onChange={handleChange} maxLength={6} required />
        </div>

        {/* AUTO FILLED */}
        {['city', 'state', 'country'].map((f) => (
          <div className="form-group" key={f}>
            <label>{f.toUpperCase()}</label>
            <input value={form[f]} readOnly />
          </div>
        ))}

        {/* WALLET */}
        <div className="form-group">
          <label>
            <img src={logo} alt="coin" style={{ width: 18, marginRight: 6 }} />
            Wallet
          </label>
          <input type="number" name="wallet" value={form.wallet} onChange={handleChange} min={0} />
        </div>

        {/* GENDER */}
        <div className="form-group">
          <label>Gender</label>
          <select name="gender" value={form.gender} onChange={handleChange} required>
            <option value="">Select</option>
            <option>Male</option>
            <option>Female</option>
            <option>Other</option>
          </select>
        </div>

        {/* PAN & AADHAAR */}
        <div className="form-group">
          <label>PAN Card</label>
          <input type="file" accept="image/*" onChange={(e) => setForm((p) => ({ ...p, panCard: e.target.files[0] }))} />
        </div>

        <div className="form-group">
          <label>Aadhaar Card</label>
          <input type="file" accept="image/*" onChange={(e) => setForm((p) => ({ ...p, aadhaarCard: e.target.files[0] }))} />
        </div>

        {/* EMERGENCY CONTACTS */}
        {[1, 2].map((n) => (
          <React.Fragment key={n}>
            <h4>Emergency Contact {n}</h4>
            {['name', 'phone', 'email', 'relation'].map((field) => (
              <div className="form-group" key={field}>
                <label>{field.toUpperCase()}</label>
                <input
                  name={`emergencyContact${n}.${field}`}
                  value={form[`emergencyContact${n}`][field]}
                  onChange={handleChange}
                  required={n === 1 && field !== 'email'}
                />
              </div>
            ))}
          </React.Fragment>
        ))}

        {/* PASSWORD */}
        <div className="form-group full-width">
          <label>Password</label>
<input
  type="password"
  name="password"
  value={form.password}
  onChange={handleChange}
  required={!editingId}
/>        </div>

        <button type="submit" className="btn-submit">
          {editingId ? 'Update' : 'Create'}
        </button>
      </form>

      {/* USERS TABLE */}
      <h2 className="title">All Users</h2>
      <table className="user-table">
        <thead>
          <tr>
            <th>Photo</th>
            <th>Name</th>
            <th>Email</th>
            <th>Mobile</th>
            {/* <th>City</th> */}
            <th>Wallet</th>
            <th>PAN</th>
            <th>Aadhaar</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>

        <tbody>
          {users.map((user) => (
            <tr key={user._id}>
              <td>
                <img src={user.profileLogo?.url || logo} alt="profile" className="profile-img" />
              </td>
              <td>{user.name}</td>
              <td>{user.email}</td>
              <td>{user.mobile}</td>
              {/* <td>{user.cityOrPincode}</td> */}
              <td>{user.wallet}</td>

             <td data-label="PAN">
  {user.panCard?.url ? (
    <img
      src={user.panCard.url}
      alt="PAN"
      className="doc-img"
      onClick={() => window.open(user.panCard.url, '_blank')}
    />
  ) : (
    <span>—</span>
  )}
</td>

<td data-label="Aadhaar">
  {user.aadhaarCard?.url ? (
    <img
      src={user.aadhaarCard.url}
      alt="Aadhaar"
      className="doc-img"
      onClick={() => window.open(user.aadhaarCard.url, '_blank')}
    />
  ) : (
    <span>—</span>
  )}
</td>


              <td>{user.status}</td>

              <td>
                <button onClick={() => handleEdit(user)}>Edit</button>
                <button onClick={() => handleDelete(user._id)}>Delete</button>

                {user.status === 'pending' && (
                  <>
                    <button onClick={() => approveUser(user._id)}>Approve</button>
                    <button onClick={() => rejectUser(user._id)}>Reject</button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
